import { faCoins, faMoneyBill, faShieldAlt, faUser } from '@fortawesome/pro-solid-svg-icons';
import { Typography } from '@mui/material';
import { useEffect } from 'react';

import Description from '../../components/Description/Description';
import Icon from '../../components/Icon/Icon';
import ListField from '../../components/ListField/ListField';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import Summary from '../../components/Summary/Summary';
import SummaryRow from '../../components/Summary/SummaryRow';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { formatNumberByThousands } from '../../helpers/formatNumberByThousands';
import { getAccountTypeTranslationKey } from '../../helpers/getAccountTypeTranslationKey';
import { useAppDispatch } from '../../hooks';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import useGetApplicationSummary from '../../hooks/useGetApplicationSummaryService';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useTranslation from '../../hooks/useTranslation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { IFieldState } from '../../store/applicationform/applicationFormTypes';

const CoApplicantHowToApplyPage = () => {
    const translate = useTranslation();
    const dispatch = useAppDispatch();
    const { coApplicantTotalSections } = usePagesConfig();
    const { currentPage } = usePagesContext();
    useComponentTracker('CoApplicantHowToApplyPage');
    const defaultPagesAction = useDefaultPagesAction();
    const summary = useGetApplicationSummary();
    useSetBannerType('bannerZero');

    useEffect(() => {
        dispatch(
            addOrUpdateFormField({
                fieldName: 'version',
                value: '1',
            } as IFieldState),
        );
    }, [dispatch]);

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={defaultPagesAction}
                currentPage={currentPage}
                currentSection={0}
                numberOfSections={coApplicantTotalSections}
                numberOfPagesInSection={0}
                currentPageInSection={0}
                sectionTitle={translate('field.section.about-you')}
            />
            <Title text={translate('page.how-to-apply.coapplicant.title')} />
            <Summary label={translate('field.section.about-the-loan')} expand={true}>
                <SummaryRow
                    value={`${formatNumberByThousands(summary?.amount.amount)} ${translate('field.currency.label')}`}
                    label={translate('field.amount.label')}
                />
                <SummaryRow
                    value={`${summary?.tenor.years ?? 0 / 12} ${
                        (summary?.tenor.years ?? 0 / 12 > 1) ? translate('years') : translate('year')
                    }`}
                    label={translate('field.tenor.label')}
                />
                <SummaryRow
                    value={`${formatNumberByThousands(summary?.monthlyCost.amount)} ${translate(
                        'field.currency.label',
                    )}`}
                    label={translate('field.monthly-cost.label')}
                />
            </Summary>

            {summary?.isConsolidation && (
                <Summary label={translate('field.section.consolidation.label')} expand={true}>
                    {summary?.selectedConsolidations
                        ?.filter((loan) => loan.applicantType === 'MAIN')
                        .map((loan, index) => (
                            <SummaryRow
                                key={index}
                                label={translate('field.consolidation.label')}
                                value={`${
                                    loan.accountType
                                        ? translate(getAccountTypeTranslationKey(loan.accountType)) + ','
                                        : ''
                                } ${formatNumberByThousands(loan.currentBalance?.amount)} ${translate(
                                    'field.currency.label',
                                )}`}
                            />
                        ))}
                </Summary>
            )}

            <Description text={translate('page.how-to-apply.description')} />
            <ListField
                name="howToApplySteps"
                hideDivider
                items={[
                    {
                        index: '1',
                        icon: <Icon icon={faUser} color="secondary" />,
                        label: translate('page.how-to-apply.subtitle.employment-details'),
                    },
                    {
                        index: '2',
                        icon: <Icon icon={faCoins} color="secondary" />,
                        label: translate('page.how-to-apply.subtitle.expenses'),
                    },
                    {
                        index: '3',
                        icon: <Icon icon={faShieldAlt} color="secondary" />,
                        label: translate('page.how-to-apply.subtitle.sign'),
                    },
                    {
                        index: '4',
                        icon: <Icon icon={faMoneyBill} color="secondary" />,
                        label: translate(
                            summary?.isConsolidation
                                ? 'page.how-to-apply.subtitle.consolidation'
                                : 'page.how-to-apply.subtitle.consolidation.optional',
                        ),
                    },
                ]}></ListField>

            {summary?.isConsolidation && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                    {translate('page.how-to-apply.coapplicant.extra-info')}
                </Typography>
            )}

            <PagesFooter
                actions={['next']}
                onAction={defaultPagesAction}
                currentSection={0}
                numberOfSections={coApplicantTotalSections}
                numberOfPagesInSection={0}
                currentPageInSection={0}
                sectionTitle={translate('field.section.about-you')}
            />
        </>
    );
};

export default CoApplicantHowToApplyPage;
